import { memo, useState, useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Components
import UserPermissionsList from 'components/UserPermissionsList';
// Import UI Components
import { Modal, Button, BusyLoader, KeyValue } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import styles from './userInfoModal.module.scss';

interface Props {
    openedModalData: {
        UserId?: number;
        UserName?: string;
        Email?: string;
        PartnerId?: number;
        Phone?: number | null;
    } | null;
    isVisibleModal: boolean;
    closeModalHandler: () => void;
    withPortal?: boolean;
}

type UserInfo = {
    Email: string;
    IsDeleted: boolean;
    IsRestricted: boolean;
    Name: string;
    Phone: number | null;
    UserId: number;
    UserName: string;
};

const UserInfoModal: FC<Props> = ({ openedModalData, isVisibleModal, closeModalHandler, withPortal = true }) => {
    const { t } = useTranslation();

    const { doGetRequest } = useRequest();

    const { getUserInfoGenericRequest: getGeneralUserInfoRequest } = useCancelableRequests({
        getUserInfoGenericRequest: UtilsHttpService.getGeneralUserInfo,
    });

    const [isLoadingUserInfo, setIsLoadingUserInfo] = useState(false);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

    const init = () => {
        setIsLoadingUserInfo(true);
        doGetRequest(getGeneralUserInfoRequest.request, {
            queryString: {
                userId: openedModalData?.UserId,
            },
            successCallback: (data) => {
                setUserInfo(data);
            },
        })?.then(() => {
            setIsLoadingUserInfo(false);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <Modal
            withPortal={withPortal}
            background="dark-background"
            position="center"
            title={t(l.Profile)}
            className={styles.userInfoModal}
            closable={false}
            closeOnClickOutside={true}
            visible={isVisibleModal}
            onCancel={closeModalHandler}
            footer={
                <Button color="default" appearance="minimal" onClick={closeModalHandler}>
                    {t(l.Close)}
                </Button>
            }
        >
            <BusyLoader isBusy={isLoadingUserInfo}>
                <div className={styles.userInfoSection}>
                    <KeyValue label={t(l.UserId)} value={userInfo?.UserId} appearance="vertical" />
                    <KeyValue label={t(l.UserName)} value={userInfo?.UserName} appearance="vertical" />
                    <KeyValue label={t(l.FullName)} value={userInfo?.Name} appearance="vertical" />
                    <KeyValue label={t(l.Email)} value={userInfo?.Email} appearance="vertical" />
                </div>
                <div className={styles.permissionsTableWrapper}>
                    <UserPermissionsList userId={userInfo?.UserId ?? null} />
                </div>
            </BusyLoader>
        </Modal>
    );
};

export default memo(UserInfoModal);
